import React, { useState, useEffect } from "react";
import {
  Plus,
  Save,
  Play,
  Pause,
  XCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Database,
  Brain,
  Server,
} from "lucide-react";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";
import { ordersApi, productsApi, clientsApi, authFetch } from "../../utils/api";
import {
  ORDER_STATUSES,
  PROVISIONING_STATUSES,
  PRODUCT_CODES,
} from "../../constants";

const StatusBadge = ({ value, options }) => {
  const opt = options.find((o) => o.value === value) || {
    label: value,
    color: "slate",
  };
  const colorMap = {
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800",
    blue: "bg-blue-100 text-blue-800",
    slate: "bg-slate-100 text-slate-800",
  };
  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${colorMap[opt.color] || colorMap.slate}`}
    >
      {opt.label}
    </span>
  );
};

const ServiceIcons = ({ productCode }) => {
  const product = PRODUCT_CODES.find((p) => p.value === productCode);
  if (!product) return <span className="text-slate-400 text-xs">-</span>;
  return (
    <div className="flex space-x-1">
      {product.rag && (
        <Database className="w-4 h-4 text-blue-600" title="RAG" />
      )}
      {product.slm && <Brain className="w-4 h-4 text-purple-600" title="SLM" />}
      {product.llm && (
        <Server className="w-4 h-4 text-amber-600" title="Private LLM" />
      )}
    </div>
  );
};

const ProvisioningSteps = ({ log }) => {
  if (!log || !Array.isArray(log) || log.length === 0) return null;
  return (
    <div className="mt-2 space-y-1">
      {log.map((step, idx) => (
        <div key={idx} className="flex items-center text-xs">
          {step.status === "completed" && (
            <CheckCircle className="w-3 h-3 text-green-500 mr-1 flex-shrink-0" />
          )}
          {step.status === "in_progress" && (
            <Clock className="w-3 h-3 text-yellow-500 mr-1 flex-shrink-0" />
          )}
          {step.status === "failed" && (
            <AlertTriangle className="w-3 h-3 text-red-500 mr-1 flex-shrink-0" />
          )}
          {step.status === "pending" && (
            <Clock className="w-3 h-3 text-slate-300 mr-1 flex-shrink-0" />
          )}
          <span className="text-slate-600">
            {step.step || step.message || "Step"}
          </span>
        </div>
      ))}
    </div>
  );
};

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingOrder, setEditingOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);

  const [formData, setFormData] = useState({
    order_number: "",
    facility_id: "",
    product_code: "",
    status: "pending",
    monthly_price: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersData, productsData, facilitiesData] = await Promise.all([
        ordersApi.getAll(),
        productsApi.getAll(),
        clientsApi.getAll(),
      ]);
      setOrders(ordersData);
      setProducts(productsData);
      setFacilities(facilitiesData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  // Auto-fill price when product is selected
  const handleProductChange = (productCode) => {
    const product = products.find(
      (p) => (p.product_code || p.productCode) === productCode,
    );
    setFormData({
      ...formData,
      product_code: productCode,
      monthly_price: product
        ? product.base_price || product.basePrice || ""
        : "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        monthly_price: parseFloat(formData.monthly_price) || 0,
      };
      if (editingOrder) {
        await ordersApi.update(editingOrder.id, payload);
      } else {
        await ordersApi.create(payload);
      }
      fetchData();
      resetForm();
    } catch (error) {
      console.error("Error saving order:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    setLoading(true);
    try {
      await ordersApi.delete(id);
      fetchData();
    } catch (error) {
      console.error("Error deleting order:", error);
    }
    setLoading(false);
  };

  const handleAction = async (action, id) => {
    const confirmMsg = {
      provision: "Start provisioning infrastructure for this order?",
      activate: "Activate this order? Services will go live.",
      suspend: "Suspend this order? Services will be paused.",
      cancel: "Cancel this order? Services will be decommissioned.",
    };
    if (!window.confirm(confirmMsg[action])) return;
    setLoading(true);
    try {
      await ordersApi[action](id);
      if (action === "provision") {
        // Real infrastructure setup (Pinecone namespace, RAG config, KB seed)
        const infra = await authFetch("/orders/provision-infra", {
          method: "POST",
          body: JSON.stringify({ order_id: id }),
        });
        if (infra?.error)
          console.error("Infra provisioning failed:", infra.error);
      }
      fetchData();
      // Catch trailing async work (KB template application etc.)
      setTimeout(fetchData, 2500);
      setTimeout(fetchData, 7000);
    } catch (error) {
      console.error(`Error ${action}ing order:`, error);
    }
    setLoading(false);
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      order_number: getField(order, "order_number", "orderNumber") || "",
      facility_id: getField(order, "facility_id", "facilityId") || "",
      product_code: getField(order, "product_code", "productCode") || "",
      status: order.status || "pending",
      monthly_price: getField(order, "monthly_price", "monthlyPrice") || "",
      notes: order.notes || "",
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      order_number: "",
      facility_id: "",
      product_code: "",
      status: "pending",
      monthly_price: "",
      notes: "",
    });
    setEditingOrder(null);
    setShowForm(false);
  };

  const getField = (obj, ...keys) => {
    for (const key of keys) {
      if (obj[key] !== undefined) return obj[key];
    }
    return undefined;
  };

  const getProductLabel = (code) => {
    const p = PRODUCT_CODES.find((pc) => pc.value === code);
    return p ? p.label : code;
  };

  const getFacilityName = (facilityId) => {
    const f = facilities.find(
      (fac) => (fac.facility_id || fac.facilityId || fac.id) === facilityId,
    );
    return f
      ? f.facility_name || f.facilityName || f.name || facilityId
      : facilityId;
  };

  const filteredOrders = orders.filter((order) => {
    const orderNum = getField(order, "order_number", "orderNumber") || "";
    const facilityId = getField(order, "facility_id", "facilityId") || "";
    const productCode = getField(order, "product_code", "productCode") || "";

    const matchesSearch =
      orderNum.toLowerCase().includes(searchTerm.toLowerCase()) ||
      facilityId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      productCode.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onRefresh={fetchData}
            placeholder="Search orders..."
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Order
        </PrimaryButton>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        {ORDER_STATUSES.map((status) => {
          const count = orders.filter((o) => o.status === status.value).length;
          const colorMap = {
            green: "border-green-200 bg-green-50",
            yellow: "border-yellow-200 bg-yellow-50",
            red: "border-red-200 bg-red-50",
            slate: "border-slate-200 bg-slate-50",
          };
          return (
            <div
              key={status.value}
              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                statusFilter === status.value
                  ? colorMap[status.color]
                  : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
              onClick={() =>
                setStatusFilter(
                  statusFilter === status.value ? "all" : status.value,
                )
              }
            >
              <div className="text-2xl font-bold text-slate-800">{count}</div>
              <div className="text-xs text-slate-500">{status.label}</div>
            </div>
          );
        })}
      </div>

      <FormModal
        title={editingOrder ? "Edit Order" : "New Order"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Order Number *
              </label>
              <input
                type="text"
                required
                value={formData.order_number}
                onChange={(e) =>
                  setFormData({ ...formData, order_number: e.target.value })
                }
                placeholder="ORD-202603-0001 (auto-generated if blank)"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Facility *
              </label>
              <select
                required
                value={formData.facility_id}
                onChange={(e) =>
                  setFormData({ ...formData, facility_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select facility...</option>
                {facilities.map((f) => {
                  const fid = f.facility_id || f.facilityId || f.id;
                  const fname =
                    f.facility_name || f.facilityName || f.name || fid;
                  return (
                    <option key={fid} value={fid}>
                      {fname} ({fid})
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Product *
              </label>
              <select
                required
                value={formData.product_code}
                onChange={(e) => handleProductChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select product...</option>
                {PRODUCT_CODES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              {formData.product_code && (
                <div className="mt-1">
                  <ServiceIcons productCode={formData.product_code} />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Monthly Price ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.monthly_price}
                onChange={(e) =>
                  setFormData({ ...formData, monthly_price: e.target.value })
                }
                placeholder="Auto-filled from product"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {editingOrder && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {ORDER_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Order"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Order #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Facility
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Services
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Provisioning
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredOrders.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No orders found. Create one to provision a product for a
                  facility.
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => {
                const orderNum =
                  getField(order, "order_number", "orderNumber") || "-";
                const facilityId =
                  getField(order, "facility_id", "facilityId") || "-";
                const productCode =
                  getField(order, "product_code", "productCode") || "-";
                const price = getField(order, "monthly_price", "monthlyPrice");
                const provStatus =
                  getField(
                    order,
                    "provisioning_status",
                    "provisioningStatus",
                  ) || "not_started";
                const provLog = getField(
                  order,
                  "provisioning_log",
                  "provisioningLog",
                );
                const isExpanded = expandedOrder === order.id;

                return (
                  <React.Fragment key={order.id}>
                    <tr
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() =>
                        setExpandedOrder(isExpanded ? null : order.id)
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-slate-900">
                        {orderNum}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        <div>{getFacilityName(facilityId)}</div>
                        <div className="text-xs text-slate-400">
                          {facilityId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {getProductLabel(productCode)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ServiceIcons productCode={productCode} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {price ? `$${parseFloat(price).toFixed(2)}/mo` : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge
                          value={order.status}
                          options={ORDER_STATUSES}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge
                          value={provStatus}
                          options={PROVISIONING_STATUSES}
                        />
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex space-x-1">
                          {order.status === "pending" && (
                            <button
                              onClick={() =>
                                handleAction("provision", order.id)
                              }
                              className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Start Provisioning"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          {order.status === "provisioning" &&
                            provStatus === "completed" && (
                              <button
                                onClick={() =>
                                  handleAction("activate", order.id)
                                }
                                className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Activate"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          {order.status === "active" && (
                            <button
                              onClick={() => handleAction("suspend", order.id)}
                              className="p-1 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                              title="Suspend"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          {order.status !== "cancelled" && (
                            <button
                              onClick={() => handleAction("cancel", order.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          <ActionButtons
                            onEdit={() => handleEdit(order)}
                            onDelete={() => handleDelete(order.id)}
                          />
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-6 py-4 bg-slate-50 border-t border-slate-100"
                        >
                          <div className="grid grid-cols-3 gap-6 text-sm">
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">
                                Order Details
                              </h4>
                              <div className="space-y-1 text-slate-600">
                                <div>
                                  Ordered:{" "}
                                  {getField(order, "ordered_at", "orderedAt")
                                    ? new Date(
                                        getField(
                                          order,
                                          "ordered_at",
                                          "orderedAt",
                                        ),
                                      ).toLocaleDateString()
                                    : "-"}
                                </div>
                                <div>
                                  Provisioned:{" "}
                                  {getField(
                                    order,
                                    "provisioned_at",
                                    "provisionedAt",
                                  )
                                    ? new Date(
                                        getField(
                                          order,
                                          "provisioned_at",
                                          "provisionedAt",
                                        ),
                                      ).toLocaleDateString()
                                    : "-"}
                                </div>
                                <div>
                                  Activated:{" "}
                                  {getField(
                                    order,
                                    "activated_at",
                                    "activatedAt",
                                  )
                                    ? new Date(
                                        getField(
                                          order,
                                          "activated_at",
                                          "activatedAt",
                                        ),
                                      ).toLocaleDateString()
                                    : "-"}
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">
                                Provisioning Log
                              </h4>
                              {provLog &&
                              Array.isArray(provLog) &&
                              provLog.length > 0 ? (
                                <ProvisioningSteps log={provLog} />
                              ) : (
                                <span className="text-slate-400 text-xs">
                                  No provisioning steps yet
                                </span>
                              )}
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">
                                Notes
                              </h4>
                              <p className="text-slate-600">
                                {order.notes || "No notes"}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderManagement;
